import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment, Header, Radio, Form } from "semantic-ui-react";
import { Field } from "formik";

import FormikSemanticDropdown from "../../forms/formik/FormikSemanticDropdown.jsx";

import { PANEL_STATES } from "../../../state/reducers/projectWizard/projectWizardConstants.js";

import {
  selectSimpleCIDROptions,
  selectFirstOctetOptions,
  selectSecondOctetOptions
} from "../../../state/reducers/projectWizard/projectWizardSelectors.js";
import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class CIDRPanel extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    limits: PropTypes.object,
    formikProps: PropTypes.object.isRequired, // required for ResourceBlock selectors to function
    apiErrors: PropTypes.object,
    disabled: PropTypes.bool.isRequired,
    copyKey: PropTypes.string.isRequired,

    firstOctetOptions: PropTypes.array,
    secondOctetOptions: PropTypes.array,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    disabled: false,
    copyKey: "project_wizard.cidr_panel"
  };

  constructor(props) {
    super(props);

    this.state = {
      showAdvanced: get(props, `formikProps.values.${props.id}Prime`, undefined)
        ? true
        : false
    };
  }

  // -- HANDLERS -- //

  onShowAdvanced = (event, data) => {
    const { id, formikProps } = this.props;

    if (!data.checked) {
      formikProps.setFieldValue(`${id}Prime`, null);
      formikProps.setFieldValue(`${id}Sub`, null);
    }

    this.setState({ showAdvanced: data.checked });
  };

  // -- RENDER -- //

  renderSummary = () => {
    const { id, formikProps } = this.props;

    const primeValue = get(formikProps, `values.${id}Prime`, null);
    const subValue = get(formikProps, `values.${id}Sub`, null);

    if (primeValue === null || subValue === null) return null;

    return (
      <strong>{`${primeValue}.${subValue}.0.0 - ${primeValue}.${subValue}.255.255`}</strong>
    );
  };

  renderPanelBody(bodyCopy) {
    const {
      id,
      apiErrors,
      simpleCIDROptions,
      firstOctetOptions,
      secondOctetOptions,
      copyKey,
      copy
    } = this.props;

    const { showAdvanced } = this.state;

    return (
      <Fragment>
        <Header as="h3" attached="top" block>
          {copy(`${copyKey}.title`)}
        </Header>

        <Segment clearing={true} attached>
          <p>{bodyCopy}</p>
          <Field
            name={`${id}`}
            label={copy(`${copyKey}.class_placeholder`)}
            placeholder={copy(`${copyKey}.class_label`)}
            disabled={showAdvanced}
            options={simpleCIDROptions}
            required={true}
            apiErrors={apiErrors}
            component={FormikSemanticDropdown}
          />

          <Form.Group>
            <Radio
              toggle
              data-testid="showAdvancedToggle"
              checked={showAdvanced}
              label="Advanced users may wish to use a more specific CIDR class."
              onClick={this.onShowAdvanced}
              style={{ marginLeft: "0.5em" }}
            />
          </Form.Group>

          {showAdvanced && (
            <Form.Group>
              <Field
                name={`${id}Prime`}
                label={copy(`${copyKey}.primary_label`)}
                placeholder={copy(`${copyKey}.primary_placeholder`)}
                options={firstOctetOptions}
                required={true}
                apiErrors={apiErrors}
                selection
                search
                compact
                width={2}
                component={FormikSemanticDropdown}
              />
              <p style={{ marginTop: "2.3em" }}>.</p>
              <Field
                name={`${id}Sub`}
                label={copy(`${copyKey}.secondary_label`)}
                placeholder={copy(`${copyKey}.secondary_placeholder`)}
                options={secondOctetOptions}
                required={true}
                apiErrors={apiErrors}
                selection
                search
                compact
                width={2}
                component={FormikSemanticDropdown}
              />
              <p style={{ marginTop: "2.3em" }}>
                {`/16   `}
                {this.renderSummary()}
              </p>
            </Form.Group>
          )}
        </Segment>
      </Fragment>
    );
  }

  renderActive() {
    const { copyKey, copy } = this.props;
    return this.renderPanelBody(copy(`${copyKey}.body`));
  }

  renderOptional() {
    const { copyKey, copy } = this.props;
    return this.renderPanelBody(copy(`${copyKey}.body_optional`));
  }

  renderInactive() {
    return null;
  }

  render() {
    // actual logic
    const { view } = this.props;

    switch (view) {
      case PANEL_STATES.ACTIVE:
        return this.renderActive();
      case PANEL_STATES.OPTIONAL:
        return this.renderOptional();
      default:
        return this.renderInactive();
    }
  }
}

const mapStateToProps = (state, props) => ({
  simpleCIDROptions: selectSimpleCIDROptions(state, props),
  firstOctetOptions: selectFirstOctetOptions(state, props),
  secondOctetOptions: selectSecondOctetOptions(state, props),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(CIDRPanel);
